/**
 * 정답 문자열을 화면에 어떻게 보여 줄지 판정한다.
 *
 * 정답은 한 줄 문자열 하나다(`answer`). 그 안에 `3` 같은 숫자도 오고
 * `\frac{12\sqrt{3}}{5}` 같은 bare LaTeX(달러 구분자 없음)도 온다.
 * 무엇이 왔는지 서버가 알려 주지 않으므로 값 자체를 보고 정한다.
 *
 * 규칙은 발명하지 않고 이 저장소의 정본을 옮겨 왔다 — src/pages/ProblemEdit.js:1836-1857.
 * 거기가 정답을 수식으로 그리는 가장 정교한 형제이고, 같은 값이 화면마다 다르게
 * 판정되면 한 정답이 자리마다 다른 얼굴이 된다.
 *
 * 다만 한 가지는 가져오지 않았다 — 길이별 폰트 축소(20자 넘으면 10px, 15자 넘으면 11px).
 * 그건 좁은 표 셀에 넣느라 생긴 값이고, 여기는 모달 우측 폭을 다 쓰는 자리라 이유가 닿지 않는다.
 * 게다가 9~11px 은 이 저장소가 금지한 크기다(CLAUDE.md 시각 위계).
 *
 * @returns {{ mode: 'empty'|'text'|'math', value: string }}
 *   empty 아직 없음 · text 원문자 객관식 정답 · math KaTeX 로 그린다.
 *   value 는 구분자를 벗긴 표시용 값이다 — 저장 값은 건드리지 않는다(원문 그대로 PATCH 된다).
 */
export function parseAnswerForDisplay(raw) {
  const s = String(raw ?? '').trim();
  if (s === '') return { mode: 'empty', value: '' };

  // 구분자 제거 — OCR·AI 가 넣는 표기가 갈려 있어 세 형태를 모두 받는다.
  // 길이 가드가 없으면 "$" 한 글자가 시작·끝 조건을 동시에 만족해 빈 값이 된다.
  let v = s;
  if (v.length >= 2 && v.startsWith('$') && v.endsWith('$')) v = v.slice(1, -1);
  else if (v.length >= 4 && v.startsWith('\\(') && v.endsWith('\\)')) v = v.slice(2, -2);
  else if (v.length >= 4 && v.startsWith('\\[') && v.endsWith('\\]')) v = v.slice(2, -2);
  v = v.trim();
  if (v === '') return { mode: 'empty', value: '' };

  // 원문자 객관식 정답('①'~'⑳')은 평문이다 — 기존 문제은행 관례(AnswerModal.js:21)가 원문자
  // 문자열을 그대로 저장·평문 표시하고, KaTeX 는 이 글리프의 메트릭이 없어(katex 0.16 실측:
  // "No character metrics" 경고 + 폴백 폰트) 수식 컨텍스트에 넣을 이유가 없다.
  if (/^[\u2460-\u2473]$/.test(v)) return { mode: 'text', value: v };

  // 정답은 편집을 마치면 한 가지 규칙으로 KaTeX 렌더링한다. 숫자만 있는 값도 예외로 빼지 않는다 —
  // OCR 검수에서 확인한 값과 문제은행에 등록된 뒤의 얼굴이 같아야 하고, 이 함수의 모든 소비자가
  // 같은 판정을 공유해야 한다. 이 판정은 표시만 바꾸며 저장되는 원문 문자열은 건드리지 않는다.
  return { mode: 'math', value: v };
}

export default parseAnswerForDisplay;
